import React from 'react';
import { useMultiplier, recordTonSpent } from './hooks/useMultiplier';
import { VIP_LEVELS } from './config/gameConfig';
import { useTon } from './TelegramTonProvider';
import { useTelegram } from './TelegramProvider';
import { PRIZE_POOL_ADDRESS } from './config';

export const VipPage = () => {
  const { balanceTier, vipLevel, totalMultiplier, totalTonSpent } = useMultiplier();
  const { connected, sendTransaction, connect } = useTon();
  const { hapticFeedback, showAlert } = useTelegram();
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleUpgradeVip = async (targetLevel) => {
    if (!connected) {
      connect?.();
      return;
    }

    const cost = targetLevel.minSpent - totalTonSpent;
    if (cost <= 0) {
      showAlert?.('You already have this VIP level!');
      return;
    }

    setIsProcessing(true);
    hapticFeedback?.('light');

    try {
      const result = await sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [{
          address: PRIZE_POOL_ADDRESS,
          amount: String(Math.floor(cost * 1e9)),
        }],
      });

      if (result?.boc) {
        recordTonSpent(cost);
        hapticFeedback?.('heavy');
        showAlert?.(`🎉 Welcome to ${targetLevel.level} VIP!`);
        window.location.reload();
      }
    } catch (err) {
      if (!err.message?.includes('rejected') && !err.message?.includes('cancelled')) {
        showAlert?.(`Error: ${err.message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const vipLevelsList = Object.values(VIP_LEVELS).slice(1);

  return (
    <div style={{ padding: '0 16px 100px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-icon" style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}>👑</div>
        <div>
          <div className="page-title">VIP Club</div>
          <div className="page-subtitle">Exclusive benefits for players</div>
        </div>
      </div>

      {/* Current Status */}
      <div style={{
        background: `linear-gradient(135deg, ${vipLevel.color}20, ${balanceTier.color}10)`,
        borderRadius: 20,
        padding: 20,
        border: `2px solid ${vipLevel.color}50`,
        marginBottom: 16,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: -30, right: -30,
          width: 120, height: 120,
          background: `radial-gradient(circle, ${vipLevel.color}30, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <div style={{
              width: 70, height: 70, borderRadius: 20,
              background: `linear-gradient(135deg, ${vipLevel.color}40, ${vipLevel.color}20)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 40,
              boxShadow: `0 0 30px ${vipLevel.color}30`,
            }}>
              {vipLevel.icon}
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>YOUR STATUS</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: vipLevel.color }}>
                {vipLevel.level}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ padding: 14, background: 'rgba(0,0,0,0.2)', borderRadius: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>VIP BONUS</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#10B981' }}>
                +{((vipLevel.multiplier - 1) * 100).toFixed(0)}%
              </div>
            </div>
            <div style={{ padding: 14, background: 'rgba(0,0,0,0.2)', borderRadius: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>TOTAL MULTIPLIER</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#FFD700' }}>
                x{totalMultiplier.toFixed(1)}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
            Total spent: <strong style={{ color: '#fff' }}>{totalTonSpent.toFixed(1)} TON</strong>
          </div>
        </div>
      </div>

      {/* VIP Tiers */}
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: 16,
        padding: 16,
        border: '1px solid var(--border-color)',
        marginBottom: 16,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: '#fff' }}>
          👑 VIP Levels
        </div>

        {vipLevelsList.map((level, i) => {
          const isCurrentOrAbove = totalTonSpent >= level.minSpent;
          const isCurrent = vipLevel.level === level.level;
          const cost = level.minSpent - totalTonSpent;

          return (
            <div key={i} style={{
              padding: 16,
              borderRadius: 14,
              background: isCurrent
                ? `linear-gradient(135deg, ${level.color}20, ${level.color}10)`
                : 'rgba(255,255,255,0.02)',
              border: isCurrent
                ? `2px solid ${level.color}50`
                : '1px solid rgba(255,255,255,0.05)',
              marginBottom: i < vipLevelsList.length - 1 ? 10 : 0,
              opacity: isCurrentOrAbove && !isCurrent ? 0.5 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 50, height: 50, borderRadius: 14,
                    background: `linear-gradient(135deg, ${level.color}30, ${level.color}15)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 26,
                  }}>
                    {level.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: level.color }}>
                      {level.level}
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                      Spend {level.minSpent}+ TON
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  {isCurrent ? (
                    <div style={{
                      padding: '6px 14px',
                      borderRadius: 20,
                      background: `${level.color}30`,
                      border: `1px solid ${level.color}50`,
                      fontSize: 12,
                      fontWeight: 700,
                      color: level.color,
                    }}>
                      ✓ Current
                    </div>
                  ) : isCurrentOrAbove ? (
                    <div style={{
                      padding: '6px 14px',
                      borderRadius: 20,
                      background: 'rgba(16,185,129,0.2)',
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#10B981',
                    }}>
                      ✓ Unlocked
                    </div>
                  ) : (
                    <button
                      onClick={() => handleUpgradeVip(level)}
                      disabled={isProcessing}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 20,
                        background: isProcessing ? '#333' : `linear-gradient(135deg, ${level.color}, ${level.color}CC)`,
                        border: 'none',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#fff',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {isProcessing ? '...' : `${cost.toFixed(0)} TON →`}
                    </button>
                  )}
                </div>
              </div>

              <div style={{
                marginTop: 12,
                padding: 10,
                background: 'rgba(0,0,0,0.2)',
                borderRadius: 8,
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}>
                <strong style={{ color: '#10B981' }}>+{((level.multiplier - 1) * 100).toFixed(0)}%</strong> bonus on all wins
              </div>
            </div>
          );
        })}
      </div>

      {/* Balance Tiers */}
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: 16,
        padding: 16,
        border: '1px solid var(--border-color)',
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: '#fff' }}>
          💎 Balance Multipliers
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Hold LILAPE to boost your wins! These stack with VIP bonuses.
        </div>

        {[
          { name: 'Standard', min: '0', mult: 'x1.0', color: '#6B7280', icon: '🐵', current: balanceTier.name === 'Standard' },
          { name: 'Bronze', min: '25B', mult: 'x1.5', color: '#CD7F32', icon: '🥉', current: balanceTier.name === 'Bronze' },
          { name: 'Silver', min: '50B', mult: 'x2.0', color: '#C0C0C0', icon: '🥈', current: balanceTier.name === 'Silver' },
          { name: 'Gold', min: '100B', mult: 'x3.0', color: '#FFD700', icon: '🥇', current: balanceTier.name === 'Gold' },
        ].map((tier, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            borderRadius: 10,
            background: tier.current ? `${tier.color}15` : 'transparent',
            border: tier.current ? `1px solid ${tier.color}40` : '1px solid transparent',
            marginBottom: i < 3 ? 8 : 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{tier.icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: tier.color }}>{tier.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Hold {tier.min}+ LILAPE</div>
              </div>
            </div>
            <div style={{
              fontSize: 18,
              fontWeight: 900,
              color: tier.current ? '#10B981' : tier.color,
            }}>
              {tier.mult}
            </div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div style={{
        marginTop: 16,
        padding: 14,
        borderRadius: 12,
        background: 'rgba(59,130,246,0.1)',
        border: '1px solid rgba(59,130,246,0.2)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#3B82F6', marginBottom: 8 }}>
          💡 How Multipliers Work
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Your <strong>Balance Tier</strong> (based on LILAPE held) and <strong>VIP Level</strong> (based on TON spent)
          multiply together. Example: Gold tier (x3) + Diamond VIP (+30%) = x3.9 total multiplier!
        </div>
      </div>
    </div>
  );
};

export default VipPage;
